import { useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useBooking } from "@/hooks/useBookings";
import { useService } from "@/hooks/useServices";
import BookingMarketplaceServiceDetail from "@/components/BookingMarketplaceServiceDetail";
import BreadcrumbNavigation from "@/components/BreadcrumbNavigation";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

export default function BookingMarketplaceServicePage() {
  const { bookingId, serviceId } = useParams<{ bookingId: string; serviceId: string }>();
  const [, setLocation] = useLocation();

  const { data: booking, loading: bookingLoading, error: bookingError } = useBooking(bookingId!);
  const { data: service, loading: serviceLoading, error: serviceError } = useService(serviceId!);

  const loading = bookingLoading || serviceLoading;
  const error = bookingError || serviceError;

  const breadcrumbItems = [
    { label: "Dashboard", href: "/dashboard" },
    { label: "Bookings", href: "/bookings" },
    { label: booking ? `${booking.activityName}` : "Loading...", href: `/bookings/${bookingId}` },
    { label: "Marketplace", href: `/bookings/${bookingId}/marketplace` },
    { label: service ? service.name : "Loading...", href: "#", current: true }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <BreadcrumbNavigation items={breadcrumbItems} />
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => setLocation(`/bookings/${bookingId}/marketplace`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Marketplace Browser
            </Button>
            <Skeleton className="h-8 w-64" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error || !booking || !service) {
    return (
      <div className="space-y-6">
        <BreadcrumbNavigation items={breadcrumbItems} />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error || "Booking or service not found"}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BreadcrumbNavigation items={breadcrumbItems} />

      <BookingMarketplaceServiceDetail
        bookingId={bookingId!}
        serviceId={serviceId!}
        onBack={() => setLocation(`/bookings/${bookingId}/marketplace`)}
      />
    </div>
  );
}